import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import { emailUpdateStatusApi, viewEmailQuoteApi } from "helpers/ApiManager";
import { ViewQuotePayload } from "types/email.api";
import { ErrorHandler, ErrorToast, SuccessToast } from "utils/toast";
import DocumentViewLayoutCopy from "Components/DocumentViewLayoutCopy";
import { regeneratePaymentLink } from "slices/thunks";
import { useAppDispatch } from "Components/Hooks/hooks";

const ViewQuote = () => {
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const token = searchParams.get("token");

  const navigate = useNavigate();

  const [quoteDetails, setQuoteDetails] = useState<ViewQuotePayload | null>(
    null,
  );
  const [isFetching, setIsFetching] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showButtons, setShowButtons] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPayDepositLoading, setIsPayDepositLoading] = useState(false);
  const [refreshQuote, setQuoteRefresh] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!quoteId || !token) {
      setIsFetching(false);
      return;
    }

    const fetchQuote = async () => {
      try {
        setIsFetching(true);
        const res = await viewEmailQuoteApi({
          quoteId: parseInt(quoteId),
          hash: token,
        });
        if (res) {
          setQuoteDetails(res?.payload);
          setShowButtons(() => {
            if (
              res.payload.quote.status === "approved" ||
              res.payload.quote.status === "rejected" ||
              res.payload.quote.status === "cancelled"
            ) {
              return false;
            }
            return true;
          });
        }
      } catch (error) {
        ErrorHandler(error);
        navigate("/dashboard");
      } finally {
        setIsFetching(false);
      }
    };

    fetchQuote();
  }, [quoteId, token, navigate, refreshQuote]);

  const columns = useMemo(
    () => [
      {
        header: "Description",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "Qty",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const { quantity, unit } = row.original;
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {quantity} {unit}
            </span>
          );
        },
      },
      {
        header: "Unit Price",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "Total",
        accessorKey: "total_price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
    ],
    [],
  );

  const handleDownload = async () => {
    if (!quoteDetails) return;
    try {
      setIsDownloading(true);
      const downloadUrl = `${quoteDetails.quote.url}?download=1`;
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${quoteDetails.quote.reference_number}.pdf`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      link.remove();
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleQuoteAction = async (status: "approved" | "rejected") => {
    if (!quoteId || !token) return;

    try {
      setIsActionLoading(true);
      await emailUpdateStatusApi({
        quoteId: quoteId,
        hash: token,
        status,
      });

      SuccessToast(
        `Quote ${status === "approved" ? "accepted" : "rejected"} successfully`,
      );

      const quote = quoteDetails?.quote;
      const paymentStatus =
        quote?.deposit_payment?.status ?? quote?.payment_status;
      const paymentId = quote?.deposit_payment?.id ?? quote?.payment_id;
      const paymentAmount =
        quote?.deposit_payment?.amount ?? quote?.payment_amount;

      if (
        (quote?.deposit_required && paymentStatus === "received") ||
        !quote?.deposit_required
      ) {
        navigate("/status-quote-invoice");
      }
      if (status === "rejected") {
        setShowButtons(false);
        setQuoteRefresh(true);
      } else {
        navigate("/pay-deposit", {
          state: {
            payment_id: paymentId,
            payment_amount: paymentAmount,
          },
        });
      }
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handlePayDeposit = async () => {
    const paymentId =
      quoteDetails?.quote?.deposit_payment?.id ??
      quoteDetails?.quote?.payment_id;
    if (!paymentId) {
      ErrorToast("Payment information is missing. Please contact support.");
      return;
    }

    try {
      setIsPayDepositLoading(true);
      const response = await dispatch(
        regeneratePaymentLink({ id: paymentId }),
      )?.unwrap();
      const url = response?.payload?.stripe_payment_link_url;
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsPayDepositLoading(false);
    }
  };

  if (isFetching) {
    return (
      <FullScreenLoader
        isLoading={isFetching}
        message="Fetching Quote Details..."
      />
    );
  }

  if (!quoteId || !token || !quoteDetails) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <FullScreenLoader
        isLoading={isActionLoading || isPayDepositLoading}
        message="Processing your action..."
      />

      <DocumentViewLayoutCopy
        quoteDetails={quoteDetails}
        title="Quote Details"
        originator={{
          name: quoteDetails.originator.name,
        }}
        company={quoteDetails?.company}
        documentNumberLabel="Quote Number"
        documentNumber={quoteDetails.quote.reference_number}
        documentDateLabel="Quote Date"
        documentDate={quoteDetails.quote.quote_date}
        dueDate={quoteDetails.quote.expiry_date}
        items={quoteDetails.quote.items}
        columns={columns}
        financialSummary={quoteDetails?.quote?.financial_summary}
        handleAction={handleQuoteAction}
        quoteInvoiceSettings={quoteDetails.quote_invoice_settings}
        btnName="Quote"
        showButtons={showButtons}
        onDownload={handleDownload}
        isDownloading={isDownloading}
        showPayDepositButton={
          !showButtons &&
          quoteDetails?.quote?.deposit_required &&
          quoteDetails?.deposit_payment?.status !== "received" &&
          quoteDetails?.quote.status !== "rejected" &&
          (quoteDetails?.deposit_payment?.payment_method ?? "cash") !== "cash"
        }
        payAmount={
          quoteDetails?.quote?.deposit_payment?.amount ??
          quoteDetails?.quote?.deposit_amount ??
          0
        }
        onPayDeposit={handlePayDeposit}
        paymentStatus={quoteDetails?.deposit_payment?.status ?? "pending"}
      />
    </>
  );
};

export default ViewQuote;
